import { useSortable } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  GripVertical,
  Trash2,
  Copy,
  Settings,
  Plus,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "../ui/button";
import { useState } from "react";
import BlockRenderer from "./BlockRenderer";

const SortableBlock = ({
  block,
  sectionId,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
  onUpdate,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: block.id,
    data: { type: "block", sectionId },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        group relative rounded-lg transition-all
        ${isDragging ? "opacity-50 z-50" : ""}
        ${isSelected ? "ring-2 ring-primary" : "hover:ring-1 hover:ring-border"}
      `}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(block.id);
      }}
    >
      {/* Block toolbar */}
      <div
        className={`
          absolute -top-8 left-0 right-0 flex items-center justify-between
          opacity-0 group-hover:opacity-100 transition-opacity z-10
          ${isSelected ? "opacity-100" : ""}
        `}
      >
        <div
          {...attributes}
          {...listeners}
          className="flex items-center gap-1 bg-background border rounded px-2 py-1 cursor-grab shadow-sm"
        >
          <GripVertical className="h-3 w-3 text-muted-foreground" />
          <span className="text-xs font-medium capitalize">{block.type}</span>
        </div>
        <div className="flex items-center gap-1 bg-background border rounded shadow-sm">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          >
            <Copy className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Block content */}
      <BlockRenderer block={block} onUpdate={onUpdate} isEditing={isSelected} />
    </div>
  );
};

const DroppableSection = ({
  section,
  children,
  isCollapsed,
  onToggleCollapse,
  onUpdateSection,
  onDeleteSection,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `droppable-${section.id}`,
    data: { sectionId: section.id, type: "section" },
  });

  return (
    <div
      ref={setNodeRef}
      className={`
        relative rounded-lg transition-all
        ${isOver ? "ring-2 ring-primary ring-dashed bg-primary/5" : ""}
      `}
      style={{
        backgroundColor: section.backgroundColor || "#ffffff",
        padding: section.padding || "20px",
      }}
    >
      {/* Section header */}
      <div className="absolute -top-6 left-0 right-0 flex items-center justify-between opacity-0 hover:opacity-100 transition-opacity">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-5 px-2 text-xs bg-background border"
            onClick={onToggleCollapse}
          >
            {isCollapsed ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronUp className="h-3 w-3 mr-1" />}
            {section.type || "Section"}
          </Button>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 bg-background border text-destructive hover:text-destructive"
          onClick={onDeleteSection}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {!isCollapsed && (
        <>
          {children}
          {section.blocks.length === 0 && (
            <div className="border-2 border-dashed border-border/50 rounded-lg p-8 text-center text-muted-foreground">
              <p className="text-sm">Drag blocks here</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const EmailCanvas = ({
  sections,
  globalStyles,
  selectedBlockId,
  onSelectBlock,
  onUpdateBlock,
  onDeleteBlock,
  onDuplicateBlock,
  onUpdateSection,
  onDeleteSection,
  onAddSection,
}) => {
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  return (
    <div
      className="rounded-lg shadow-lg overflow-hidden"
      style={{ backgroundColor: globalStyles.backgroundColor }}
      onClick={() => onSelectBlock(null)}
    >
      <div
        className="mx-auto"
        style={{
          maxWidth: globalStyles.contentWidth,
          backgroundColor: globalStyles.contentBackgroundColor,
        }}
      >
        {sections.map((section, index) => (
          <div key={section.id} className="relative group">
            <SortableContext
              items={section.blocks.map((b) => b.id)}
              strategy={verticalListSortingStrategy}
            >
              <DroppableSection
                section={section}
                isCollapsed={collapsedSections.has(section.id)}
                onToggleCollapse={() => toggleSectionCollapse(section.id)}
                onUpdateSection={(updates) => onUpdateSection(section.id, updates)}
                onDeleteSection={() => onDeleteSection(section.id)}
              >
                <div className="space-y-4">
                  {section.blocks.map((block) => (
                    <SortableBlock
                      key={block.id}
                      block={block}
                      sectionId={section.id}
                      isSelected={selectedBlockId === block.id}
                      onSelect={onSelectBlock}
                      onDelete={() => onDeleteBlock(section.id, block.id)}
                      onDuplicate={() => onDuplicateBlock(section.id, block.id)}
                      onUpdate={(updates) => onUpdateBlock(section.id, block.id, updates)}
                    />
                  ))}
                </div>
              </DroppableSection>
            </SortableContext>
          </div>
        ))}

        {/* Add Section Button */}
        <div className="p-4 flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddSection("content")}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Section
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailCanvas;
